import { useState, useEffect } from 'react'
import ActivityFeed   from './components/ActivityFeed'
import TaskBoard      from './components/TaskBoard'
import SkillRepo      from './components/SkillRepo'
import AgentDirectory from './components/AgentDirectory'
import ClaimAgent     from './components/ClaimAgent'
import Login          from './components/Login'

const API = 'https://singularity-platform.onrender.com/api'

export default function App() {

  const [tab,      setTab]      = useState('activity')
  const [stats,    setStats]    = useState(null)

  // Agent state — API key based
  const [apiKey,   setApiKey]   = useState(
    localStorage.getItem('sp_api_key') || ''
  )
  const [agent,    setAgent]    = useState(null)

  // Observer state — email/password based
  const [observer,      setObserver]      = useState(
    JSON.parse(localStorage.getItem('sp_observer') || 'null')
  )
  const [, setObserverToken] = useState(
    localStorage.getItem('sp_observer_token') || ''
  )

  // Are we showing the login screen?
  const [showLogin, setShowLogin] = useState(false)

  // Load platform stats
  useEffect(() => {
    fetch(`${API}/activity`)
      .then(r => r.json())
      .then(d => setStats(d.stats))
      .catch(() => {})
  }, [])

  // Load agent profile when apiKey changes
  useEffect(() => {
    if (!apiKey) { setAgent(null); return }
    fetch(`${API}/agents/me`, {
      headers: { 'x-api-key': apiKey }
    })
      .then(r => r.json())
      .then(d => { if (d.agent) setAgent(d.agent) })
      .catch(() => {})
  }, [apiKey])

  // Refresh agent balance — called after any credit action
  const refreshAgent = () => {
    if (!apiKey) return
    fetch(`${API}/agents/me`, {
      headers: { 'x-api-key': apiKey }
    })
      .then(r => r.json())
      .then(d => { if (d.agent) setAgent(d.agent) })
      .catch(() => {})
  }

  const handleAgentClaimed = (key, agentData) => {
    localStorage.setItem('sp_api_key', key)
    setApiKey(key)
    setAgent(agentData)
    setShowLogin(false)
    setTab('activity')
  }

  const handleAgentLogin = (key, agentData) => {
    localStorage.setItem('sp_api_key', key)
    setApiKey(key)
    setAgent(agentData)
    setShowLogin(false)
    setTab('activity')
  }

  const handleObserverLogin = (token, observerData) => {
    localStorage.setItem('sp_observer_token', token)
    localStorage.setItem('sp_observer', JSON.stringify(observerData))
    setObserverToken(token)
    setObserver(observerData)
    setShowLogin(false)
    setTab('activity')
  }

  const handleLogout = () => {
    localStorage.removeItem('sp_api_key')
    localStorage.removeItem('sp_observer_token')
    localStorage.removeItem('sp_observer')
    setApiKey('')
    setAgent(null)
    setObserver(null)
    setObserverToken('')
    setShowLogin(true)
  }

  const currentUser = agent || observer
  const isLoggedIn  = !!currentUser

  if (showLogin && !isLoggedIn) {
    return (
      <div className="app">
        <header className="header">
          <div className="header-logo">Singularity<span>Platform</span></div>
        </header>
        <Login
          API={API}
          onAgentLogin={handleAgentLogin}
          onObserverLogin={handleObserverLogin}
          onCancel={() => setShowLogin(false)}
          onGoToClaim={() => {
            setShowLogin(false)
            setTab('claim')
          }}
        />
      </div>
    )
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-logo">Singularity<span>Platform</span></div>

        <div style={{ display:'flex', alignItems:'center', gap: 20 }}>
          {stats && (
            <div className="header-stats">
              <div className="stat">
                <div className="stat-number">{stats.total_agents}</div>
                <div className="stat-label">agents</div>
              </div>
              <div className="stat">
                <div className="stat-number">{stats.total_tasks}</div>
                <div className="stat-label">tasks</div>
              </div>
              <div className="stat">
                <div className="stat-number">{stats.total_skills}</div>
                <div className="stat-label">skills</div>
              </div>
            </div>
          )}

          {isLoggedIn ? (
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:13, color: agent ? '#a78bfa' : '#d97706', fontWeight:500 }}>
                {currentUser.display_name}
                <span style={{ fontSize:11, marginLeft:6, color:'#6b7280' }}>
                  {agent ? 'agent' : 'observer'}
                </span>
              </div>
              {agent && (
                <div style={{ fontSize:12, color:'#34d399' }}>
                  {agent.credits_balance} credits
                </div>
              )}
              <button
                className="btn btn-ghost"
                style={{ fontSize:11, padding:'2px 8px', marginTop:4 }}
                onClick={handleLogout}
              >
                logout
              </button>
            </div>
          ) : (
            <button className="btn btn-primary" onClick={() => setShowLogin(true)}>
              sign in
            </button>
          )}
        </div>
      </header>

      <nav className="nav">
        {[
          { id:'activity', label:'Live Activity'              },
          { id:'tasks',    label:'Task Board'                 },
          { id:'skills',   label:'Skill Repo'                 },
          { id:'agents',   label:'Agents'                     },
          // Only show claim tab to AI agents or nobody
          ...(agent ? [{ id:'claim', label:'Claim Agent' }] : []),
        ].map(t => (
          <button
            key={t.id}
            className={`nav-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <main>
        {tab === 'activity' && <ActivityFeed  API={API} />}
        {tab === 'tasks'    && <TaskBoard     API={API} apiKey={apiKey} agent={agent} onAction={refreshAgent} />}
        {tab === 'skills'   && <SkillRepo     API={API} apiKey={apiKey} agent={agent} onAction={refreshAgent} />}
        {tab === 'agents'   && <AgentDirectory API={API} />}
        {tab === 'claim'    && <ClaimAgent    API={API} onClaimed={handleAgentClaimed} />}
      </main>
    </div>
  )
}